import { api } from './api'

export interface User {
  id: number
  name: string
  email: string
  roles: Array<{
    id: number
    name: string
    organization_id: number
  }>
  invitation_accepted_at?: string
  invitation_sent_at?: string
  first_sign_in_at?: string
  last_sign_in_at?: string
  created_at: string
  updated_at: string
}

export interface CreateUserRequest {
  email: string
  name: string
  role: string
}

export interface UpdateUserRequest {
  name?: string
  role?: string
}

// Organization user management service
// Uses the organization user management API endpoints

export const usersService = {
  // Get current authenticated user
  async getCurrentUser(): Promise<User> {
    const response = await api.get('/user')
    return response.data
  },

  // Get users for an organization
  async getOrganizationUsers(_orgSlug: string): Promise<User[]> {
    try {
      const response = await api.get(`/users/`)
      return response.data.users || []
    } catch (error: any) {
      console.error('Failed to fetch organization users:', error)
      return []
    }
  },

  // Invite user to organization
  async inviteUser(_orgSlug: string, userData: CreateUserRequest): Promise<User> {
    try {
      const response = await api.post(`/users/`, userData)
      return response.data.user
    } catch (error: any) {
      console.error('Failed to invite user:', error)
      if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail)
      }
      throw new Error('Failed to invite user')
    }
  },

  // Update user role/details
  async updateUser(_orgSlug: string, userId: number, userData: UpdateUserRequest): Promise<User> {
    try {
      const response = await api.put(`/users/${userId}`, userData)
      return response.data.user
    } catch (error: any) {
      console.error('Failed to update user:', error)
      if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail)
      }
      throw new Error('Failed to update user')
    }
  },

  // Remove user from organization
  async removeUser(_orgSlug: string, userId: number): Promise<void> {
    try {
      await api.delete(`/users/${userId}`)
    } catch (error: any) {
      console.error('Failed to remove user:', error)
      if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail)
      }
      throw new Error('Failed to remove user')
    }
  },

  // Resend invitation email to user
  async resendInvite(_orgSlug: string, userId: number): Promise<void> {
    try {
      await api.post(`/users/${userId}/resend-invite`)
    } catch (error: any) {
      console.error('Failed to resend invite:', error?.response?.data || error)
      // Extract error message from API response
      const detail = error.response?.data?.detail
      if (detail) {
        throw new Error(detail)
      }
      // Fallback for network errors
      throw new Error(error.message || 'Failed to resend invitation')
    }
  }
}